import { useState } from 'react'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface DateRangePresetProps {
  onDateRangeChange: (startDate: Date, endDate: Date) => void
  className?: string
  excludePresets?: string[]
  dateOffsetDays?: number
  value?: string
  onValueChange?: (value: string) => void
  /** When provided, all preset end dates are capped to this date. */
  maxDate?: Date
}

// If max is provided and end > max, return max instead.
const capDate = (date: Date, max?: Date): Date =>
  max && date > max ? new Date(max) : date

// Date calculation utilities
const getMonthToDate = (offsetDays: number = 0): { start: Date; end: Date } => {
  const today = new Date()
  // Set to noon to avoid timezone issues when converting to ISO string
  const start = new Date(today.getFullYear(), today.getMonth(), 1, 12, 0, 0)
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0)
  
  // Apply offset to end date
  if (offsetDays > 0) {
    end.setDate(end.getDate() - offsetDays)
  }
  
  return { start, end }
}

const getYearToDate = (offsetDays: number = 0): { start: Date; end: Date } => {
  const today = new Date()
  const start = new Date(today.getFullYear(), 0, 1, 12, 0, 0)
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0)
  
  // Apply offset to end date
  if (offsetDays > 0) {
    end.setDate(end.getDate() - offsetDays)
  }
  
  return { start, end }
}

const getLastWeek = (offsetDays: number = 0): { start: Date; end: Date } => {
  const today = new Date()
  const currentDay = today.getDay()
  const daysToMonday = currentDay === 0 ? 6 : currentDay - 1
  
  // Get last Monday
  const lastMonday = new Date(today)
  lastMonday.setDate(today.getDate() - daysToMonday - 7)
  lastMonday.setHours(12, 0, 0, 0)
  
  // Get last Sunday
  const lastSunday = new Date(lastMonday)
  lastSunday.setDate(lastMonday.getDate() + 6)
  lastSunday.setHours(12, 0, 0, 0)
  
  // Apply offset to both dates
  if (offsetDays > 0) {
    lastMonday.setDate(lastMonday.getDate() - offsetDays)
    lastSunday.setDate(lastSunday.getDate() - offsetDays)
  }
  
  return { start: lastMonday, end: lastSunday }
}

const getLastMonth = (offsetDays: number = 0): { start: Date; end: Date } => {
  const today = new Date()
  // Set to noon to avoid timezone issues
  const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1, 12, 0, 0)
  const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0, 12, 0, 0)
  
  // Apply offset to end date
  if (offsetDays > 0) {
    lastDayLastMonth.setDate(lastDayLastMonth.getDate() - offsetDays)
  }
  
  return { start: firstDayLastMonth, end: lastDayLastMonth }
}

export default function DateRangePreset({ 
  onDateRangeChange, 
  className = '', 
  excludePresets = [], 
  dateOffsetDays = 0,
  value,
  onValueChange,
  maxDate
}: DateRangePresetProps) {
  const { t } = useTranslation('components')
  const [internalValue, setInternalValue] = useState('')
  
  // Use external value if provided, otherwise use internal state
  const currentValue = value !== undefined ? value : internalValue
  
  const handlePresetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value
    
    if (!selectedValue) return // Placeholder selected
    
    let dateRange: { start: Date; end: Date }
    
    switch (selectedValue) {
      case 'month-to-date':
        dateRange = getMonthToDate(dateOffsetDays)
        break
      case 'year-to-date':
        dateRange = getYearToDate(dateOffsetDays)
        break
      case 'last-week':
        dateRange = getLastWeek(dateOffsetDays)
        break
      case 'last-month':
        dateRange = getLastMonth(dateOffsetDays)
        break
      default:
        return
    }

    // Cap the end date to maxDate if provided
    dateRange = { ...dateRange, end: capDate(dateRange.end, maxDate) }
    
    // Update value (either internal or notify parent)
    if (onValueChange) {
      onValueChange(selectedValue)
    } else {
      setInternalValue(selectedValue)
    }
    
    // Trigger date range change
    onDateRangeChange(dateRange.start, dateRange.end)
  }
  
  return (
    <div className={`relative ${className}`}>
      <select
        onChange={handlePresetChange}
        value={currentValue}
        className="input-field pl-10 cursor-pointer"
      >
        <option value="">{t('dateRangePreset.placeholder')}</option>
        {!excludePresets.includes('month-to-date') && (
          <option value="month-to-date">{t('dateRangePreset.monthToDate')}</option>
        )}
        {!excludePresets.includes('year-to-date') && (
          <option value="year-to-date">{t('dateRangePreset.yearToDate')}</option>
        )}
        {!excludePresets.includes('last-week') && (
          <option value="last-week">{t('dateRangePreset.lastWeek')}</option>
        )}
        {!excludePresets.includes('last-month') && (
          <option value="last-month">{t('dateRangePreset.lastMonth')}</option>
        )}
      </select>
      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
    </div>
  )
}
